/**
 * Conflict Resolution Utilities
 * Provides intelligent suggestions for resolving scheduling conflicts
 */

import { createLocalDateTime, addMinutes, timePeriodsOverlap } from './timezone';

/**
 * Find next available time slot for a specific staff member
 * @param {string} staffId - Staff member ID
 * @param {string} currentDate - Current appointment date (YYYY-MM-DD)
 * @param {string} currentTime - Current appointment time (HH:MM)
 * @param {number} duration - Appointment duration in minutes
 * @param {Array} existingAppointments - All existing appointments
 * @param {Array} timeOptions - Available time slots for the day
 * @returns {Object} Next available slot or null
 */
export function findNextAvailableSlot(staffId, currentDate, currentTime, duration, existingAppointments, timeOptions) {
  if (!staffId || !currentDate || !timeOptions.length) return null;

  // Get current time index
  const currentTimeIndex = timeOptions.indexOf(currentTime);
  if (currentTimeIndex === -1) return null;

  // Check each time slot after current time
  for (let i = currentTimeIndex + 1; i < timeOptions.length; i++) {
    const testTime = timeOptions[i];
    
    if (isTimeSlotAvailable(staffId, currentDate, testTime, duration, existingAppointments)) {
      return {
        time: testTime,
        date: currentDate,
        message: `Next available: ${testTime} (same day)`
      };
    }
  }

  // If no slots today, check next day (simplified - could be enhanced)
  const nextDate = getNextBusinessDay(currentDate);
  for (const testTime of timeOptions.slice(0, Math.min(6, timeOptions.length))) { // Check first 6 slots of next day
    if (isTimeSlotAvailable(staffId, nextDate, testTime, duration, existingAppointments)) {
      return {
        time: testTime,
        date: nextDate,
        message: `Next available: ${testTime} (${formatDateRelative(nextDate)})`
      };
    }
  }

  return null;
}

/**
 * Find multiple available time slots for a specific staff member
 * @param {string} staffId - Staff member ID
 * @param {string} currentDate - Current appointment date (YYYY-MM-DD)
 * @param {string} currentTime - Current appointment time (HH:MM)
 * @param {number} duration - Appointment duration in minutes
 * @param {Array} existingAppointments - All existing appointments
 * @param {Array} timeOptions - Available time slots for the day
 * @param {number} maxSlots - Maximum number of slots to return (default: 10)
 * @returns {Array} Array of available slots
 */
export function findMultipleAvailableSlots(staffId, currentDate, currentTime, duration, existingAppointments, timeOptions, maxSlots = 10) {
  if (!staffId || !currentDate || !timeOptions.length) return [];

  const availableSlots = [];
  const currentTimeIndex = timeOptions.indexOf(currentTime);
  
  // Start checking from the slot after current time
  let startIndex = currentTimeIndex >= 0 ? currentTimeIndex + 1 : 0;
  
  // Check remaining slots today
  for (let i = startIndex; i < timeOptions.length && availableSlots.length < maxSlots; i++) {
    const testTime = timeOptions[i];
    
    if (isTimeSlotAvailable(staffId, currentDate, testTime, duration, existingAppointments)) {
      availableSlots.push({
        time: testTime,
        date: currentDate,
        message: `${testTime} (today)`,
        displayTime: testTime,
        displayDate: 'Today',
        sortOrder: 1
      });
    }
  }

  // Check next few days if we need more slots
  let daysToCheck = 7; // Check up to 7 days ahead
  let checkDate = currentDate;
  
  for (let day = 1; day <= daysToCheck && availableSlots.length < maxSlots; day++) {
    checkDate = getNextBusinessDay(checkDate);
    
    // Check all time slots for this day
    for (const testTime of timeOptions) {
      if (availableSlots.length >= maxSlots) break;
      
      if (isTimeSlotAvailable(staffId, checkDate, testTime, duration, existingAppointments)) {
        const relativeDate = formatDateRelative(checkDate);
        availableSlots.push({
          time: testTime,
          date: checkDate,
          message: `${testTime} (${relativeDate})`,
          displayTime: testTime,
          displayDate: relativeDate,
          sortOrder: day + 1
        });
      }
    }
  }

  return availableSlots.slice(0, maxSlots);
}

/**
 * Find alternative rooms available at the same time
 * @param {string} currentRoomId - Currently selected room ID
 * @param {string} date - Appointment date
 * @param {string} time - Appointment time
 * @param {number} duration - Duration in minutes
 * @param {Array} existingAppointments - All existing appointments
 * @param {Array} allRooms - All available rooms
 * @returns {Array} Available alternative rooms
 */
export function findAlternativeRooms(currentRoomId, date, time, duration, existingAppointments, allRooms) {
  if (!date || !time || !allRooms.length) return [];

  return allRooms
    .filter(room => room.id !== currentRoomId) // Exclude current room
    .filter(room => isRoomAvailable(room.id, date, time, duration, existingAppointments))
    .map(room => ({
      id: room.id,
      name: room.name,
      message: `Available: ${room.name}`
    }));
}

/**
 * Check if a time slot is available for a staff member
 */
function isTimeSlotAvailable(staffId, date, time, duration, existingAppointments) {
  const appointmentStart = createLocalDateTime(date, time);
  const appointmentEnd = addMinutes(appointmentStart, duration);

  return !existingAppointments.some(existing => {
    if (existing.status === 'cancelled' || existing.staffId !== staffId) return false;
    if (!existing.date || !existing.time || !existing.duration) return false;

    const existingStart = createLocalDateTime(existing.date, existing.time);
    const existingEnd = addMinutes(existingStart, existing.duration);

    return timePeriodsOverlap(appointmentStart, appointmentEnd, existingStart, existingEnd);
  });
}

/**
 * Check if a room is available at a specific time
 */
function isRoomAvailable(roomId, date, time, duration, existingAppointments) {
  const appointmentStart = createLocalDateTime(date, time);
  const appointmentEnd = addMinutes(appointmentStart, duration);

  return !existingAppointments.some(existing => {
    if (existing.status === 'cancelled' || existing.roomId !== roomId) return false;
    if (!existing.date || !existing.time || !existing.duration) return false;

    const existingStart = createLocalDateTime(existing.date, existing.time);
    const existingEnd = addMinutes(existingStart, existing.duration);

    return timePeriodsOverlap(appointmentStart, appointmentEnd, existingStart, existingEnd);
  });
}

/**
 * Get next business day (simplified - assumes Mon-Fri)
 */
function getNextBusinessDay(dateString) {
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  
  // Skip weekends (simplified logic)
  if (date.getDay() === 0) date.setDate(date.getDate() + 1); // Skip Sunday
  if (date.getDay() === 6) date.setDate(date.getDate() + 2); // Skip Saturday
  
  return date.toISOString().split('T')[0];
}

/**
 * Format date relative to today
 */
function formatDateRelative(dateString) {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === tomorrow.toDateString()) {
    return 'tomorrow';
  }
  
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

/**
 * Get detailed conflict information with solutions
 * @param {Array} conflicts - Conflicts array from conflict detection
 * @param {Object} appointmentData - Current appointment data
 * @param {Array} existingAppointments - All existing appointments
 * @param {Array} staffList - All staff members
 * @param {Array} roomList - All rooms
 * @param {Array} timeOptions - Available time slots
 * @returns {Object} Detailed conflict info with solutions
 */
export function getDetailedConflictInfo(conflicts, appointmentData, existingAppointments, staffList, roomList, timeOptions) {
  if (!conflicts.length) return null;

  const conflictDetails = [];
  let nextAvailableSlot = null;
  let alternativeRooms = [];

  conflicts.forEach(conflictGroup => {
    conflictGroup.conflicts.forEach(conflict => {
      const staff = staffList.find(s => s.id === conflict.conflictWith.staffId);
      const room = roomList.find(r => r.id === conflict.conflictWith.roomId);
      const conflictTime = conflict.conflictWith.time;
      const conflictClient = conflict.conflictWith.clientName || 'Unknown Client';

      if (conflict.type === 'clinician') {
        const staffName = staff ? (staff.name || staff.fullName || 'Unknown Staff') : 'Unknown Staff';
        conflictDetails.push({
          type: 'clinician',
          title: `${staffName} is already booked`,
          description: `Conflicts with ${conflictClient} at ${conflictTime}`,
          severity: 'high'
        });

        // Find next available slot for this staff member
        if (!nextAvailableSlot && appointmentData.staffId) {
          nextAvailableSlot = findNextAvailableSlot(
            appointmentData.staffId,
            appointmentData.date,
            appointmentData.time,
            appointmentData.duration,
            existingAppointments,
            timeOptions
          );
        }
      }

      if (conflict.type === 'room') {
        const roomName = room ? room.name : 'Unknown Room';
        conflictDetails.push({
          type: 'room',
          title: `${roomName} is already in use`,
          description: `Conflicts with ${conflictClient} at ${conflictTime}`,
          severity: 'medium'
        });

        // Find alternative rooms
        if (alternativeRooms.length === 0 && appointmentData.roomId) {
          alternativeRooms = findAlternativeRooms(
            appointmentData.roomId,
            appointmentData.date,
            appointmentData.time,
            appointmentData.duration,
            existingAppointments,
            roomList
          );
        }
      }
    });
  });

  // Get multiple available slots for enhanced UX
  let multipleSlots = [];
  if (appointmentData.staffId) {
    multipleSlots = findMultipleAvailableSlots(
      appointmentData.staffId,
      appointmentData.date,
      appointmentData.time,
      appointmentData.duration,
      existingAppointments,
      timeOptions,
      10 // Get up to 10 slots
    );
  }

  return {
    conflicts: conflictDetails,
    solutions: {
      nextAvailableSlot,
      multipleAvailableSlots: multipleSlots,
      alternativeRooms: alternativeRooms.slice(0, 3) // Limit to 3 suggestions
    }
  };
}
